import { ApiError, fetchCustomers, fetchNewBookingAvailability, createBookingForCustomer, } from '../../../api/adminApi';
import { fetchPlaces } from '../../../api/placesApi';
import { i18nService } from '../../../i18n/I18nService';
import { t, formatDurationFromMinutes, isUnauthorized, isNotFound, createSelectField, createModalOverlay, createModalButton, } from './context';
import { bookableWindows, createAvailabilityStatusRow, createSlotPicker } from '../slotPicker';
const BOOK_ERROR_KEYS = {
    slot_unavailable: 'admin.bookings.bookOnBehalfModal.errors.slotUnavailable',
    day_closed: 'admin.bookings.bookOnBehalfModal.errors.dayClosed',
    no_payment_option: 'admin.bookings.bookOnBehalfModal.errors.noPaymentOption',
};
function customerLabel(customer) {
    return `${customer.surname} ${customer.name}`;
}
function placeLabel(place) {
    return place.name[i18nService.getLocale()];
}
function createTitle(text) {
    const title = document.createElement('h2');
    title.className = 'admin__modal-title';
    title.textContent = text;
    return title;
}
// One step of the wizard: a title, a labelled select filled once `load`
// resolves, and "Cancel" / "Continue" ("Continue" enabled once something is
// picked). Shared by the customer and the workspace steps.
function openSelectStep(ctx, opts) {
    const { overlay, modal } = createModalOverlay(ctx, 'admin__modal admin__modal--confirm');
    const body = document.createElement('div');
    body.className = 'admin__book-select';
    body.textContent = t('admin.bookings.bookOnBehalfModal.loading');
    const actions = document.createElement('div');
    actions.className = 'admin__modal-confirm-actions';
    let items = [];
    let select = null;
    const cancelButton = createModalButton('cta', t('admin.bookings.bookOnBehalfModal.cancel'), () => overlay.remove());
    const continueButton = createModalButton('cta admin__submit', t('admin.bookings.bookOnBehalfModal.continue'), () => {
        const picked = items.find((item) => String(item.id) === select?.value);
        if (!picked)
            return;
        overlay.remove();
        opts.onContinue(picked);
    });
    continueButton.disabled = true;
    actions.append(cancelButton, continueButton);
    modal.append(createTitle(t('admin.bookings.bookOnBehalfModal.title')), body, actions);
    opts
        .load()
        .then((loaded) => {
        items = loaded;
        const field = createSelectField(opts.label, opts.placeholder, loaded, opts.optionLabel);
        field.select.addEventListener('change', () => {
            continueButton.disabled = field.select.value === '';
        });
        select = field.select;
        body.textContent = '';
        body.appendChild(field.wrapper);
    })
        .catch((err) => {
        if (isUnauthorized(err)) {
            overlay.remove();
            ctx.showLogin();
            return;
        }
        console.error('Failed to load book-on-behalf options:', err);
        body.textContent = t('admin.bookings.bookOnBehalfModal.loadError');
    });
}
// "Book on behalf": customer → workspace → time, for the calendar's selected
// `date` (`YYYY-MM-DD`). `onBooked` re-loads the calendar after a booking.
export function openBookOnBehalfModal(ctx, date, onBooked) {
    openSelectStep(ctx, {
        label: t('admin.bookings.bookOnBehalfModal.customerLabel'),
        placeholder: t('admin.bookings.bookOnBehalfModal.customerPlaceholder'),
        load: fetchCustomers,
        optionLabel: customerLabel,
        onContinue: (customer) => openSelectStep(ctx, {
            label: t('admin.bookings.bookOnBehalfModal.placeLabel'),
            placeholder: t('admin.bookings.bookOnBehalfModal.placePlaceholder'),
            load: () => fetchPlaces('available'),
            optionLabel: placeLabel,
            onContinue: (place) => openTimeStep(ctx, { customer, place, date, onBooked }),
        }),
    });
}
// The last step: the "Change time" picker for the chosen place and day, with
// the customer's subscription balance and how the booking will be paid.
function openTimeStep(ctx, target) {
    const { customer, place, date, onBooked } = target;
    const { overlay, modal } = createModalOverlay(ctx, 'admin__modal');
    const [year, month, day] = date.split('-');
    const text = document.createElement('p');
    text.className = 'admin__modal-confirm-text';
    text.textContent = t('admin.bookings.bookOnBehalfModal.timeText', {
        customer: customerLabel(customer),
        place: placeLabel(place),
        date: `${day}.${month}.${year}`,
    });
    const body = document.createElement('div');
    body.className = 'admin__reschedule';
    const error = document.createElement('p');
    error.className = 'admin__error';
    error.hidden = true;
    const actions = document.createElement('div');
    actions.className = 'admin__modal-confirm-actions';
    const cancelButton = createModalButton('cta', t('admin.bookings.bookOnBehalfModal.cancelTime'), () => overlay.remove());
    const bookButton = createModalButton('cta admin__submit', t('admin.bookings.bookOnBehalfModal.book'), () => book());
    bookButton.disabled = true;
    actions.append(cancelButton, bookButton);
    modal.append(createTitle(t('admin.bookings.bookOnBehalfModal.timeTitle')), text, body, error, actions);
    let availability = null;
    // The picker owns the current start/end — read it with picker.getSelection()
    // rather than keeping a second copy in sync via onChange.
    let picker = null;
    function currentSelection() {
        return picker?.getSelection() ?? { start: null, end: null };
    }
    function showError(message) {
        error.textContent = message;
        error.hidden = false;
    }
    function showMessage(message) {
        body.innerHTML = '';
        const line = document.createElement('p');
        line.className = 'booking__empty';
        line.textContent = message;
        body.appendChild(line);
        bookButton.disabled = true;
    }
    function load(errorAfterLoad) {
        showMessage(t('admin.bookings.bookOnBehalfModal.loading'));
        fetchNewBookingAvailability(customer.id, place.id, date)
            .then((response) => {
            availability = response;
            render(response);
            if (errorAfterLoad)
                showError(errorAfterLoad);
        })
            .catch((err) => {
            if (isUnauthorized(err)) {
                overlay.remove();
                ctx.showLogin();
                return;
            }
            console.error('Failed to load book-on-behalf availability:', err);
            showMessage(t(isNotFound(err) ? 'admin.bookings.bookOnBehalfModal.errors.notFound' : 'admin.bookings.bookOnBehalfModal.loadError'));
        });
    }
    function render(response) {
        body.innerHTML = '';
        body.appendChild(createAvailabilityStatusRow(date, response.stepMinutes));
        const windows = bookableWindows(response.windows);
        if (response.closed || windows.length === 0) {
            picker = null;
            const message = document.createElement('p');
            message.className = 'booking__empty';
            message.textContent = response.closed
                ? t('admin.bookings.bookOnBehalfModal.closed')
                : t('admin.bookings.bookOnBehalfModal.noWindows');
            body.appendChild(message);
            bookButton.disabled = true;
            return;
        }
        picker = createSlotPicker({
            windows,
            stepMinutes: response.stepMinutes,
            onChange: () => {
                error.hidden = true;
                renderPayment();
            },
        });
        const payment = document.createElement('div');
        payment.className = 'admin__book-payment';
        body.append(picker.element, payment);
        renderPayment();
    }
    // How the chosen interval will be paid — mirrors the server's decision:
    // the subscription if its balance covers the whole booking, otherwise
    // hourly (if the place has a rate at all; if not, "Book" stays disabled).
    function renderPayment() {
        if (!availability)
            return;
        const payment = body.querySelector('.admin__book-payment');
        if (!payment)
            return;
        payment.innerHTML = '';
        const addLine = (className, message) => {
            const line = document.createElement('p');
            line.className = className;
            line.textContent = message;
            payment.appendChild(line);
        };
        const leftMinutes = availability.subscriptionLeftMinutes;
        if (leftMinutes !== null) {
            addLine('booking__summary', t('admin.bookings.bookOnBehalfModal.balance', { time: formatDurationFromMinutes(leftMinutes) }));
        }
        const { start, end } = currentSelection();
        if (start === null || end === null) {
            bookButton.disabled = true;
            return;
        }
        const coveredBySubscription = leftMinutes !== null && leftMinutes >= end - start;
        if (!coveredBySubscription && !availability.hasHourlyRate) {
            addLine('admin__error admin__book-warning', t('admin.bookings.bookOnBehalfModal.noPaymentOption'));
            bookButton.disabled = true;
            return;
        }
        if (!coveredBySubscription) {
            addLine('admin__error admin__book-warning', t(leftMinutes === null
                ? 'admin.bookings.bookOnBehalfModal.noSubscription'
                : 'admin.bookings.bookOnBehalfModal.insufficientHourly'));
        }
        bookButton.disabled = false;
    }
    function book() {
        const { start, end } = currentSelection();
        if (start === null || end === null || bookButton.disabled)
            return;
        error.hidden = true;
        bookButton.disabled = true;
        cancelButton.disabled = true;
        bookButton.classList.add('is-loading');
        createBookingForCustomer({ userId: customer.id, placeId: place.id, date, startMinutes: start, endMinutes: end })
            .then(() => {
            overlay.remove();
            onBooked();
        })
            .catch((err) => {
            bookButton.classList.remove('is-loading');
            cancelButton.disabled = false;
            if (isUnauthorized(err)) {
                overlay.remove();
                ctx.showLogin();
                return;
            }
            const code = err instanceof ApiError ? err.code : undefined;
            const errorKey = isNotFound(err)
                ? 'admin.bookings.bookOnBehalfModal.errors.notFound'
                : (code && BOOK_ERROR_KEYS[code]) || 'admin.bookings.bookOnBehalfModal.errors.generic';
            if (code === 'slot_unavailable') {
                // Someone else booked meanwhile — re-fetch the free windows.
                load(t(errorKey));
                return;
            }
            console.error('Failed to book on behalf of customer:', err);
            showError(t(errorKey));
            bookButton.disabled = false;
        });
    }
    load();
}
